import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { css, cx } from 'emotion';
import Flexbox from 'flexbox-react';
import _ from 'lodash';
import InfoBoxLabel from 'components/core/InfoBoxLabel';
import RIEInput from 'components/core/editable/RIEInput';
import RIESecondsInput from 'components/core/editable/RIESecondsInput';
import ProcessRunLocationsField from 'components/core/fields/ProcessRunLocationsField';
import ProcessDataLocationsField from 'components/core/fields/ProcessDataLocationsField';
import ProcessOutputSettingsField from 'components/core/fields/ProcessOutputSettingsField';
import ProcessLogSettingsField from 'components/core/fields/ProcessLogSettingsField';
import ProcessService from 'services/ProcessService';
import InfoMessages from 'components/core/help/InfoMessages';
import ProcessUtils from 'utils/ProcessUtils';
import Constants from 'utils/Constants';
import { colorTextGrey, colorVeryLightGrey } from 'styles/base';
import DataExplorerService from 'services/DataExplorerService';

const field = css`
    padding: 15px 20px;
    .infobox .form-label {
        width: 140px;
    }
`;

const grey = css`
    background-color: ${colorVeryLightGrey}
`;

const runLocationsField = css`
    .infobox .form-label {
        width: 140px;
    }
    .editable-wrapper {
        min-width: 565px !important; // override the editable value for this form
    }
    .Select-control {
        width: 565px;
    }
`;

class RunDataConsolidatorForm extends React.Component {
    static propTypes = {
        processId: PropTypes.string,
        process: PropTypes.object,
        processErrors: PropTypes.object, // the process validation errors
        runParameters: PropTypes.object,
        runParameterErrors: PropTypes.object,
        siteFacilities: PropTypes.array,
        showTransformationMessage: PropTypes.bool,
        hasWritePermissions: PropTypes.bool,
        rangeStatus: PropTypes.string // identifies what to do with range parameter
    };

    onEmailUpdated = (fieldPath, value) => {
        const parameters = _.cloneDeep(this.props.runParameters);
        parameters.contact_email = value;
        ProcessService.updateRunParameters(this.props.process.name, parameters);
    };

    onStartDateUpdated = (fieldPath, value) => {
        const parameters = _.cloneDeep(this.props.runParameters);
        parameters.start_date = value;
        ProcessService.updateRunParameters(this.props.process.name, parameters);
    };

    onEndDateUpdated = (fieldPath, value) => {
        const parameters = _.cloneDeep(this.props.runParameters);
        parameters.end_date = value;
        ProcessService.updateRunParameters(this.props.process.name, parameters);
    };

    onRangeUpdated = (fieldPath, value) => {
        if (value === 0) {
            // If value is 0, then change that to the default value of LENGTH_OF_PROCESSING_INTERVAL
            value = Constants.RANGE_DEFAULT;
        }
        const parameters = _.cloneDeep(this.props.runParameters);
        parameters.time_range = value;
        ProcessService.updateRunParameters(this.props.process.name, parameters);
    };

    renderRangeIfNeeded(labelWidth, rowMarginBottom) {
        const messageStyle = css`
            font-size: 12px;
            font-style: italic;
            color: ${colorTextGrey};
        `;
        const infoBoxStyle = css`
            align-self: flex-start !important;
        `;
        const flexboxStyle = css`
            width: 100%;
        `;
        const coordinateSystemNames = ProcessUtils.getCoordinateSystemNames(this.props.process);
        const exploredDatastreams = DataExplorerService.getExploredDatastreams(this.props.process.name);
        const isRelevant = ProcessUtils.isRelevantTransformationParameter(this.props.process, 'range', coordinateSystemNames, 'time', exploredDatastreams);

        if (this.props.rangeStatus === 'irrelevant') {
            // Range isn't relevant, so don't show anything
            return (<div/>);

        } else if (this.props.rangeStatus === 'overridable') {
            // User has not specified a range value in the PCM, so they can override it in the conf files when running
            // the data consolidator.
            return (
                <Flexbox flexDirection="row" alignItems="flex-start" marginBottom={rowMarginBottom}>
                    <InfoBoxLabel
                        label="Range (time)"
                        width={labelWidth}
                        message={InfoMessages.range}
                        className={infoBoxStyle}
                    />
                    <Flexbox flexDirection="column" className={flexboxStyle}>
                        <RIESecondsInput
                            value={this.props.runParameters.time_range}
                            change={this.onRangeUpdated}
                            fieldPath={['time_range']}
                            processId={this.props.process.name}
                            processErrors={this.props.runParameterErrors}
                            isFullWidth={false}
                            formDisplay
                        />
                        <div className={messageStyle}>
                            *Range can be overriden in the data consolidator because a default value has not been set in the PCM.
                        </div>
                    </Flexbox>
                </Flexbox>
            );

        } else {
            // Otherwise, show message that says that range has been defined in the PCM, so it can't be overridden here.
            return (
                <Flexbox flexDirection="row" alignItems="flex-start" marginBottom={rowMarginBottom} className={flexboxStyle}>
                    <InfoBoxLabel
                        label="Range (time)"
                        width={labelWidth}
                        message={InfoMessages.range}
                        className={infoBoxStyle}
                    />
                    <div className={messageStyle}>
                        *A default range value has been set in the PCM, so it cannot be overriden in the data consolidator.
                        Please navigate to the range transformation parameter to change the default value.
                    </div>
                </Flexbox>
            );
        }
    }

    renderForm() {
        const labelWidth = '135px';
        const labelWidth2 = '110px';
        const rowMarginBottom = '10px';
        return (
            <Flexbox flexDirection="column">
                <Flexbox flexDirection="row" alignItems="flex-start" className={cx(field, grey)}>
                    <Flexbox flexDirection="column" paddingRight={'5px'}>
                        {/* Contact Email */}
                        <Flexbox flexDirection="row" alignItems="flex-start"
                                 marginBottom={rowMarginBottom} paddingRight={'9px'}>
                            <InfoBoxLabel
                                label="Contact Email"
                                width={labelWidth2}
                                message={InfoMessages.runContactEmail}
                            />
                            <RIEInput
                                value={this.props.runParameters.contact_email}
                                change={this.onEmailUpdated}
                                fieldPath={['contact_email']}
                                isDisabled={false}
                                processId={this.props.process.name}
                                processErrors={this.props.runParameterErrors}
                                formDisplay
                            />
                        </Flexbox>
                        <ProcessRunLocationsField
                            className={runLocationsField}
                            labelWidth="105px"
                            process={this.props.process}
                            runParameters={this.props.runParameters}
                            runParameterErrors={this.props.runParameterErrors}
                            hasWritePermissions
                            isOwner={this.props.hasWritePermissions}
                            siteFacilities={this.props.siteFacilities}
                            forceFormDisplay
                        />
                    </Flexbox>
                    <Flexbox flexDirection="column" paddingLeft={'5px'}>
                        {/* Start Date */}
                        <Flexbox flexDirection="row" alignItems="flex-start" marginBottom={rowMarginBottom}>
                            <InfoBoxLabel
                                label="Start Date"
                                width={labelWidth2}
                                message={InfoMessages.startDate}
                            />
                            <RIEInput
                                value={this.props.runParameters.start_date}
                                change={this.onStartDateUpdated}
                                fieldPath={['start_date']}
                                isDisabled={false}
                                processId={this.props.process.name}
                                processErrors={this.props.runParameterErrors}
                                formDisplay
                            />
                        </Flexbox>
                        {/* End Date */}
                        <Flexbox flexDirection="row" alignItems="center" height="40px" marginBottom={rowMarginBottom}>
                            <InfoBoxLabel
                                label="End Date"
                                width={labelWidth2}
                                message={InfoMessages.endDate}
                            />
                            <RIEInput
                                value={this.props.runParameters.end_date}
                                change={this.onEndDateUpdated}
                                fieldPath={['end_date']}
                                isDisabled={false}
                                processId={this.props.process.name}
                                processErrors={this.props.runParameterErrors}
                                formDisplay
                            />
                        </Flexbox>
                    </Flexbox>
                </Flexbox>

                <ProcessDataLocationsField
                    className={cx(field)}
                    labelWidth="145px"
                    process={this.props.process}
                    runParameters={this.props.runParameters}
                    runParameterErrors={this.props.runParameterErrors}
                    hasWritePermissions
                    siteFacilities={this.props.siteFacilities}
                    forceFormDisplay
                />
                <ProcessLogSettingsField
                    className={cx(field, grey)}
                    labelWidth="145px"
                    process={this.props.process}
                    runParameters={this.props.runParameters}
                    runParameterErrors={this.props.runParameterErrors}
                    hasWritePermissions
                    siteFacilities={this.props.siteFacilities}
                    forceFormDisplay
                />
                <ProcessOutputSettingsField
                    className={cx(field)}
                    labelWidth="145px"
                    process={this.props.process}
                    runParameters={this.props.runParameters}
                    runParameterErrors={this.props.runParameterErrors}
                    hasWritePermissions
                    siteFacilities={this.props.siteFacilities}
                    forceFormDisplay
                />
                <Flexbox flexDirection="column" className={cx(field, grey)}>
                    {/* Range (for time dimension) */}
                    {this.renderRangeIfNeeded(labelWidth2, rowMarginBottom)}
                </Flexbox>
            </Flexbox>
        );
    }

    render() {
        if (this.props.runParameters) {
            return this.renderForm();
        } 
        return <div/>;
    }
}

// Connect to store so that we can get changes when RIE components are modified, even when rendered inside a modal dialog
const mapStateToProps = (storeState, ownProps) => {
    const processId = ownProps.processId;

    const processState = ProcessService.getMyState(storeState);
    const process = processState.workingCopiesById[processId];
    const processErrors = processState.processErrorsById[processId];
    const runParameters = processState.runParametersById[processId].parameters;
    const runParameterErrors = processState.runParametersById[processId].errors;
 
    return {
        process,
        processErrors,
        runParameters,
        runParameterErrors
    };
};


export default connect(mapStateToProps)(RunDataConsolidatorForm);



// WEBPACK FOOTER //
// ./src/components/core/forms/RunDataConsolidatorForm.js